import { activateSeald } from "@recare/core/model/utils/featureFlags";
import {
  Account,
  AppType,
  EncryptionContext,
  EnvContext,
  SEALD_FLOW_TYPES,
} from "@recare/core/types";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTracking } from "react-tracking";
import { getSealdSDKInstance, importIdentity } from ".";
import { createGroup, CreateGroupInContextProps } from "./groups";
import { createSession, CreateSessionInContextProps } from "./sessions";
import { UpdateSealdRegistered } from "./utils";

type SealdContextType =
  | {
      createSealdGroup: CreateSealdGroup;
      createSealdSession: CreateSealdSession;
      importSealdIdentity: ImportSealdIdentity;
      isActive: boolean;
    }
  | undefined;

const SealdContext = React.createContext<SealdContextType>(undefined);

export const useSealdContext = () => useContext(SealdContext);

export type CreateSealdGroup = (
  props: CreateGroupInContextProps,
) => Promise<void>;

export type CreateSealdSession = (
  props: CreateSessionInContextProps,
) => Promise<EncryptionContext | undefined>;

type ImportIdentityInContextProps = {
  account: Account;
  challenge: string | undefined;
  password?: string;
  seald_flow: SEALD_FLOW_TYPES | null;
  token: string;
  updateAccount: UpdateSealdRegistered;
};

export type ImportSealdIdentity = (
  props: ImportIdentityInContextProps,
) => Promise<true | undefined>;

function useImportIdentityWhenLogged({
  app,
  importSealdIdentity,
  loggedAccount,
  onLogout,
}: {
  app: AppType;
  importSealdIdentity: ImportSealdIdentity;
  loggedAccount: Account | undefined;
  onLogout: () => void;
}) {
  const [restoring, setRestoring] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    console.log("...");
    async function restore() {
      if (await getSealdSDKInstance()) {
        setActive(true);
      } else if (activateSeald(app) && loggedAccount?.id) {
        setRestoring(true);

        try {
          await importSealdIdentity({
            account: loggedAccount,
            challenge: undefined,
            token: "",
            seald_flow: null,
            updateAccount: null,
          });
          setActive(Boolean(await getSealdSDKInstance()));
        } catch (err: any) {
          err.message = `Seald restore id failed - account id ${loggedAccount.id}: ${err.message}`;
          console.error(err.message, err);
          onLogout();
        }

        setRestoring(false);
      }
    }

    restore();
  }, [loggedAccount?.id, importSealdIdentity, onLogout]);

  return { restoring, isActive: active };
}

export default function SealdContextProvider({
  app,
  children,
  envContext,
  loggedAccount,
  onLogout,
}: {
  app: AppType;
  children: React.ReactNode;
  envContext: EnvContext;
  loggedAccount: Account | undefined;
  onLogout: () => void;
}) {
  const { trackEvent } = useTracking();

  const importIdentityInContext = useCallback(
    ({
      account,
      challenge,
      password,
      seald_flow,
      token,
      updateAccount,
    }: ImportIdentityInContextProps) =>
      importIdentity({
        account,
        seald_flow,
        challenge,
        password,
        token,
        updateAccount,
        // in context
        envContext,
        trackEvent,
      }),
    [envContext],
  );

  const { isActive, restoring } = useImportIdentityWhenLogged({
    loggedAccount,
    importSealdIdentity: importIdentityInContext,
    app,
    onLogout,
  });

  const createGroupInContext = useCallback(
    ({
      createEncryptionContext,
      createSealdAccess,
      entity,
      entityAccounts,
      entityType,
      initiateSealdIdentity,
    }: CreateGroupInContextProps) =>
      createGroup({
        entityType,
        entity,
        entityAccounts,
        initiateSealdIdentity,
        createEncryptionContext,
        createSealdAccess,
        // in context
        loggedAccount,
        envContext,
        trackEvent,
        app,
      }),
    [loggedAccount, envContext, app],
  );

  const createSealdSessionInContext = useCallback(
    ({
      createEncryptionContext,
      createSealdAccess,
      entity,
      entityType,
      entityUsersDisplayIds,
    }: CreateSessionInContextProps) =>
      createSession({
        entityType,
        entity,
        entityUsersDisplayIds,
        createEncryptionContext,
        createSealdAccess,
        // in context
        loggedAccount,
        envContext,
        app,
      }),
    [loggedAccount, envContext, app],
  );

  if (restoring) return null;

  return (
    <SealdContext.Provider
      value={{
        importSealdIdentity: importIdentityInContext,
        createSealdGroup: createGroupInContext,
        createSealdSession: createSealdSessionInContext,
        isActive,
      }}
    >
      {children}
    </SealdContext.Provider>
  );
}
